/*
 * Ovarana — circular hero slideshow.
 *
 * Two things Dawn's slideshow cannot do that we need here:
 *
 *   1. Navigation by clicking the left/right half of a slide, with no arrows.
 *   2. Circular motion. Dawn wraps from the last slide to the first by
 *      scrolling backwards past every slide, which reads as "jumping back to
 *      the start". Here, next from the last slide keeps travelling rightward
 *      into the first, and prev from the first travels leftward into the last.
 *
 * How the loop works: the track always shows the slides in the order
 * [previous, current, next] and sits scrolled to the middle one. Stepping is
 * therefore always a single slide's travel in the direction you asked for, in
 * either direction, forever. Once a swipe settles we work out which slide
 * landed in view, re-order the track around it, and silently reset the scroll
 * to the middle — invisible, because the slide under the viewport does not
 * move. Swipe, trackpad, click, dot and keyboard all funnel through the same
 * two operations, so every gesture leaves the carousel in the same state.
 *
 * The re-ordering is done with the flex `order` property, not by moving nodes.
 * appendChild on a node already in the document removes and re-inserts it,
 * which restarts every CSS animation in that subtree and repaints the slide.
 * Writing `order` changes only layout, so the wrap is genuinely invisible, and
 * the DOM order stays fixed — which in turn means nothing else in the section
 * has to care that the carousel shuffles itself.
 *
 * We take the section over from Dawn rather than fight it. Dawn's
 * SliderComponent bails out when it cannot find an element whose id starts
 * with "Slider-", and SlideshowComponent bails when there is no
 * ".slider-buttons" wrapper. The Liquid withholds both in this mode, so
 * Dawn's constructor does nothing while its stylesheet still applies.
 */
(function () {
  /* A click that travelled further than this was a drag, not a tap. */
  const DRAG_TOLERANCE_PX = 10;
  /* Quiet period after the last scroll event before we look at where we are.
     Only a fallback: where scrollend exists we settle on it directly. Short,
     because nothing is committed until the track has actually stopped - a
     check that costs nothing to repeat. */
  const SETTLE_MS = 80;
  /* How far off a slide boundary still counts as "parked on that slide".
     Fractional device pixels and browser zoom both leave scrollLeft a hair
     off a whole number of slides. */
  const ALIGN_TOLERANCE_PX = 2;
  /* A trackpad emits no pointer events, so the only sign a two-finger swipe is
     still in progress is that wheel events are still arriving. Re-centring
     inside that window would yank the track out from under the gesture. */
  const WHEEL_IDLE_MS = 180;
  /* When to start checking that a step actually landed. Stepping must not
     depend on scroll events alone, but this only schedules the check - the
     settle itself still waits for the track to stop moving. */
  const STEP_SETTLE_MS = 700;
  /* Give up waiting for an un-aligned track to come to rest. Guards against a
     scroll that never quite reaches a snap point, which would otherwise keep
     rescheduling forever. Generous: a flick on a phone can coast for a couple
     of seconds, and being wrong here means committing mid-scroll. */
  const MAX_SETTLE_RETRIES = 40;
  /* How long a one-slide step takes. The browser's own `behavior: smooth`
     scales its duration with distance and spends the last third of it barely
     moving, which reads as the slide struggling to stop. A fixed, shorter
     travel with a tighter curve lands cleanly. Tune here. */
  const STEP_DURATION_MS = 300;

  class OvaranaSlideshow {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('[id^="OvaranaSlider-"]');
      if (!this.track) return;

      this.slides = Array.from(this.track.querySelectorAll('[id^="OvaranaSlide-"]'));
      if (this.slides.length < 2) return;

      this.dots = Array.from(root.querySelectorAll('.slider-counter__link'));
      this.prevButton = root.querySelector('button[name="previous"]');
      this.nextButton = root.querySelector('button[name="next"]');
      this.autoplayButton = root.querySelector('.slideshow__autoplay');

      this.count = this.slides.length;
      /* Two slides cannot fill [previous, current, next] without showing the
         same slide twice, so looping needs at least three. Below that we fall
         back to a plain clamped slider. */
      this.circular = this.count >= 3;
      this.current = 0;
      this.settleTimer = null;
      this.settleRetries = 0;
      this.stepTimer = null;
      this.stepRetries = 0;
      this.lastRestCheck = null;
      this.stepFrame = null;
      this.stepDirection = 0;
      this.pointerStart = null;
      this.gesturing = false;
      this.lastWheel = 0;

      this.track.addEventListener('scroll', () => this.scheduleSettle(), { passive: true });
      /* Where supported this is the exact signal; elsewhere the debounce above
         stands in. Both land on the same idempotent settle. */
      if ('onscrollend' in window) {
        /* The exact signal, so act on it rather than waiting out the debounce
           again - that delay is dead time in which the track is still parked
           at the end of its travel and the next swipe cannot move. */
        this.track.addEventListener('scrollend', () => this.onSettle());
      }
      this.track.addEventListener(
        'wheel',
        () => {
          this.lastWheel = Date.now();
          this.scheduleSettle();
        },
        { passive: true }
      );

      this.track.addEventListener('pointerdown', this.onPointerDown.bind(this));
      this.track.addEventListener('pointermove', this.onPointerMove.bind(this));
      this.track.addEventListener('pointerleave', this.onPointerLeave.bind(this));
      this.track.addEventListener('click', this.onClick.bind(this));
      /* The finger can lift outside the track, and the browser cancels the
         pointer outright the moment it decides the gesture is a scroll. Both
         end the gesture, and both have to be heard on the window. */
      this.onPointerEnd = this.onPointerEnd.bind(this);
      window.addEventListener('pointerup', this.onPointerEnd);
      window.addEventListener('pointercancel', this.onPointerEnd);

      this.root.addEventListener('keydown', this.onKeyDown.bind(this));

      if (this.prevButton) this.prevButton.addEventListener('click', () => this.step(-1));
      if (this.nextButton) this.nextButton.addEventListener('click', () => this.step(1));
      this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.goTo(index)));

      /* Re-centre if the viewport resizes under us. */
      this.resizeObserver = new ResizeObserver(() => this.normalize());
      this.resizeObserver.observe(this.track);

      this.normalize();
      this.updateDots();
      this.setupAutoplay();

      /* Exposed so the carousel can be inspected from the console when
         something looks wrong on the live site. */
      root.ovaranaSlideshow = this;
      root.dataset.tapNavReady = 'true';
    }

    get slideWidth() {
      return this.track.clientWidth;
    }

    /* Which position in the track the current slide occupies, in slides. */
    get restingPosition() {
      return this.circular ? 1 : this.current;
    }

    /* ---- track order ---- */

    /* Lay the slides out as [previous, current, next], with any slides beyond
       those three parked after them, off-screen. Pure layout: no node moves,
       so no animation restarts and no slide repaints. */
    layout() {
      if (this.circular) {
        this.layoutFrom((this.current - 1 + this.count) % this.count);
        return;
      }
      this.slides.forEach((slide, index) => {
        const value = String(index);
        if (slide.style.order !== value) slide.style.order = value;
      });
    }

    /* Lay the slides out in authoring order starting from `startIndex`, so
       that slide occupies the leftmost position and the rest follow it round.
       The resting layout is the window starting one before the current slide,
       which puts the current slide in the middle with a neighbour either side. */
    layoutFrom(startIndex) {
      for (let i = 0; i < this.count; i += 1) {
        const slide = this.slides[(startIndex + i) % this.count];
        const value = String(i);
        if (slide.style.order !== value) slide.style.order = value;
      }
    }

    /* Which slide is sitting at a given position in the track. */
    slideIndexAtPosition(position) {
      return this.slides.findIndex((slide) => Number(slide.style.order) === position);
    }

    /* Re-order the track around the current slide and park on it without
       animating. Silent: the slide under the viewport does not move.

       No suppression flag is needed. Re-normalizing an already-normalized
       carousel writes the same order values and the same scrollLeft it already
       has, and assigning an unchanged scrollLeft fires no scroll event, so the
       cycle stops on its own after one pass. */
    normalize() {
      if (!this.slideWidth) return;
      this.layout();
      this.scrollTo(this.restingPosition * this.slideWidth);
    }

    /* Travel one slide under our own easing rather than the browser's.

       `behavior: smooth` gives no control over duration or curve: Chrome
       scales the duration with the distance and eases out so gently that the
       last third of the move crawls. Driving scrollLeft ourselves keeps the
       motion the same length every time and lets it arrive.

       Snapping is suspended for the duration. `scroll-snap-type: mandatory`
       re-snaps every scrollLeft we write, which would pin the track to the
       slide we are leaving and the animation would never move. */
    animateTo(from, to) {
      const width = this.slideWidth;
      const startLeft = from * width;
      const distance = (to - from) * width;

      this.cancelAnimation();

      if (this.prefersReducedMotion()) {
        this.scrollTo(to * width);
        this.updateDots();
        this.normalize();
        return;
      }

      const snap = this.track.style.scrollSnapType;
      this.track.style.scrollSnapType = 'none';
      const started = performance.now();

      const finish = () => {
        this.stepFrame = null;
        this.track.style.scrollSnapType = snap;
        this.updateDots();
        this.normalize();
      };

      const frame = (now) => {
        const progress = Math.min(1, (now - started) / STEP_DURATION_MS);
        /* Ease-out cubic: quick to leave, settled by the end rather than
           asymptotically approaching it. */
        const eased = 1 - Math.pow(1 - progress, 3);
        this.scrollTo(startLeft + distance * eased);
        if (progress < 1) {
          this.stepFrame = requestAnimationFrame(frame);
        } else {
          finish();
        }
      };

      this.stepFrame = requestAnimationFrame(frame);
    }

    cancelAnimation() {
      if (this.stepFrame === null || this.stepFrame === undefined) return;
      cancelAnimationFrame(this.stepFrame);
      this.stepFrame = null;
      /* Whatever it was mid-flight, snapping has to come back on. */
      this.track.style.scrollSnapType = '';
    }

    prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    /* Jump without animating, regardless of the stylesheet's smooth default. */
    scrollTo(left) {
      const previous = this.track.style.scrollBehavior;
      this.track.style.scrollBehavior = 'auto';
      this.track.scrollLeft = left;
      this.track.style.scrollBehavior = previous;
    }

    /* ---- stepping ---- */

    step(direction) {
      if (!this.slideWidth) return;
      this.restartAutoplay();

      /* A second step while one is still moving continues from where that one
         has got to, rather than slamming it shut and starting again. Only in
         the same direction: reversing needs a slide on the other side that the
         travelling window does not have, and the plain path below - which
         lands the first step and starts afresh - already handles that. */
      if (this.circular && this.stepFrame !== null && direction === this.stepDirection) {
        this.chainStep(direction);
        return;
      }
      this.stepDirection = direction;

      /* Land any step still in flight before starting the next one, so a
         second click while the first is animating steps on from where the
         first was going rather than repeating it. Idempotent when the
         carousel is already at rest. */
      this.cancelPendingSettle();
      this.normalize();

      const from = this.restingPosition;
      if (this.circular) {
        this.current = (this.current + direction + this.count) % this.count;
      } else {
        this.current = Math.min(Math.max(this.current + direction, 0), this.count - 1);
      }
      this.updateDots();

      const target = this.circular ? from + direction : this.current;
      /* The neighbour is already laid out either side of us, so this is a
         plain one-slide travel — the same motion in both directions, and the
         same motion whether or not we are wrapping around the ends. */
      this.animateTo(from, target);

      /* A backstop in case no scroll/scrollend event ever arrives - those are
         the browser's to emit, and a missed one would strand the track
         off-centre. It goes through the settle handler rather than
         re-centring outright: a one-slide smooth scroll can outlast this
         timer, and hard-assigning scrollLeft mid-animation cuts the motion
         short with a visible jerk a few pixels from the end. The settle
         waits for the track to actually come to rest. */
      this.stepTimer = setTimeout(() => this.onStepEnd(), STEP_SETTLE_MS);
    }

    /* Continue an in-flight step into the next slide without stopping.

       The track only ever lays out as many slots as there are slides, so the
       slide we are continuing into has no slot yet - it is currently sitting
       behind us as the one we just left. Slide the whole window one place in
       the direction of travel to bring it round to the front, then take the
       same amount back off the scroll position so that nothing on screen
       moves: the view is identical, but there is now somewhere to go. */
    chainStep(direction) {
      const width = this.slideWidth;
      const position = this.track.scrollLeft / width - direction;

      this.layoutFrom((this.current - 1 + this.count) % this.count);
      this.scrollTo(position * width);

      this.current = (this.current + direction + this.count) % this.count;
      this.stepDirection = direction;
      this.updateDots();

      /* The window moved with us, so the slide we are heading for is always at
         the far end of it in the direction of travel. */
      this.animateTo(position, 1 + direction);
    }

    goTo(index) {
      if (index === this.current) return;

      if (this.circular) {
        const forward = (index - this.current + this.count) % this.count;
        if (forward === 1) return this.step(1);
        if (forward === this.count - 1) return this.step(-1);
      }

      this.restartAutoplay();
      this.cancelPendingSettle();
      this.current = index;
      this.updateDots();
      this.normalize();
    }

    /* ---- coming to rest ---- */

    scheduleSettle() {
      if (this.settleTimer) clearTimeout(this.settleTimer);
      this.settleTimer = setTimeout(() => this.onSettle(), SETTLE_MS);
    }

    cancelPendingSettle() {
      this.cancelAnimation();
      if (this.settleTimer) clearTimeout(this.settleTimer);
      if (this.stepTimer) clearTimeout(this.stepTimer);
      this.settleTimer = null;
      this.stepTimer = null;
      this.settleRetries = 0;
      this.stepRetries = 0;
      this.lastRestCheck = null;
    }

    /* Has the track stopped moving? Everything below hangs on this. Acting
       while a finger is still down, or while trackpad momentum is still
       arriving, drags the view back to the slide the user is trying to leave
       - which is exactly what a half-finished swipe used to do.

       Stopped means parked on a slide boundary, or - and this is the case
       that rescues a gesture abandoned mid-slide - not having moved since the
       last look. Without the second test a track stranded between two slides
       stays there, which is what the hero did after a swipe. */
    hasComeToRest() {
      if (this.gesturing) return false;
      if (Date.now() - this.lastWheel < WHEEL_IDLE_MS) return false;

      const left = this.track.scrollLeft;
      const position = left / this.slideWidth;
      const aligned = Math.abs(position - Math.round(position)) * this.slideWidth <= ALIGN_TOLERANCE_PX;
      const stalled =
        this.lastRestCheck !== null && Math.abs(left - this.lastRestCheck) <= ALIGN_TOLERANCE_PX;
      this.lastRestCheck = left;

      return aligned || stalled;
    }

    /* Where a swipe, a trackpad gesture or momentum comes to rest. What is on
       screen is the authority here: we adopt whichever slide actually landed,
       rather than trusting what we last asked for. */
    onSettle() {
      /* Clear rather than merely drop the handle: onSettle is normally reached
         from this timer, but anything that calls it directly must not leave a
         second pass armed behind it. */
      if (this.settleTimer) clearTimeout(this.settleTimer);
      this.settleTimer = null;
      if (!this.slideWidth) return;

      if (!this.hasComeToRest() && this.settleRetries < MAX_SETTLE_RETRIES) {
        this.settleRetries += 1;
        this.scheduleSettle();
        return;
      }
      this.commit();
    }

    /* Adopt whichever slide is actually on screen and re-arm the track around
       it. Split out of onSettle because a new gesture needs to be able to do
       this immediately, without waiting for the old one to settle. */
    commit() {
      this.cancelAnimation();
      this.settleRetries = 0;
      this.lastRestCheck = null;

      const index = this.slideIndexAtPosition(Math.round(this.track.scrollLeft / this.slideWidth));
      if (index !== -1 && index !== this.current) {
        this.current = index;
        /* A swipe is an interaction like any other: it should reset the
           autoplay countdown, not leave it about to fire mid-gesture. */
        this.restartAutoplay();
      }

      this.updateDots();
      this.normalize();
    }

    /* Where a step lands. The opposite authority to onSettle: a step already
       decided which slide it wants, so this only re-centres the track around
       that decision. Reading the position back instead would let a smooth
       scroll that has not started yet - a background tab, a browser ignoring
       the behaviour - silently undo the step. It still waits for the track to
       stop: hard-assigning scrollLeft mid-animation cuts the motion short a
       few pixels from the end, with a jerk you can see. */
    onStepEnd() {
      this.stepTimer = null;
      if (!this.slideWidth) return;

      if (!this.hasComeToRest() && this.stepRetries < MAX_SETTLE_RETRIES) {
        this.stepRetries += 1;
        this.stepTimer = setTimeout(() => this.onStepEnd(), SETTLE_MS);
        return;
      }
      this.stepRetries = 0;
      this.lastRestCheck = null;
      /* If we got here with a frame still queued the animation stalled - a
         throttled background tab is the usual reason. Drop it, or it will
         resume from a stale start point the moment the tab comes back. */
      this.cancelAnimation();

      this.updateDots();
      this.normalize();
    }

    updateDots() {
      this.dots.forEach((dot, index) => {
        const active = index === this.current;
        dot.classList.toggle('slider-counter__link--active', active);
        if (active) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });
    }

    /* ---- pointer, click-a-half and keyboard navigation ---- */

    onPointerDown(event) {
      /* The track only ever holds [previous, current, next], so straight after
         a swipe it is sitting at the very end of its travel with nothing left
         to scroll into. Until it is re-centred the next swipe cannot move it -
         which is what made quick repeated swipes feel dead, and slow ones feel
         like they needed a pause first.

         A finger landing stops whatever momentum the browser had, so this is
         exactly the moment to finish the previous gesture: commit where it got
         to and re-arm the track, so this swipe starts centred with a slide
         either side of it. */
      if (this.stepTimer) {
        /* A step still animating already decided where it is going, and it is
           only part-way there. Land it on its own terms - reading the position
           back could round to the slide it is leaving and quietly drop it. */
        this.cancelPendingSettle();
        this.updateDots();
        this.normalize();
      } else if (this.settleTimer) {
        this.cancelPendingSettle();
        this.commit();
      }

      this.gesturing = true;
      this.pointerStart = { x: event.clientX, y: event.clientY };
    }

    onPointerEnd() {
      if (!this.gesturing) return;
      this.gesturing = false;
      /* The scroll may still be gliding on momentum; onSettle waits it out. */
      this.scheduleSettle();
    }

    onPointerMove(event) {
      if (event.pointerType !== 'mouse') return;
      const half = this.isRightHalf(event) ? 'right' : 'left';
      if (this.track.dataset.tapHalf !== half) this.track.dataset.tapHalf = half;
    }

    onPointerLeave() {
      delete this.track.dataset.tapHalf;
    }

    isRightHalf(event) {
      const bounds = this.track.getBoundingClientRect();
      return event.clientX - bounds.left > bounds.width / 2;
    }

    onClick(event) {
      if (event.target.closest('a, button, input, select, textarea, summary, [role="link"]')) return;

      if (this.pointerStart) {
        const movedX = Math.abs(event.clientX - this.pointerStart.x);
        const movedY = Math.abs(event.clientY - this.pointerStart.y);
        this.pointerStart = null;
        /* A swipe already navigated by scrolling; do not count it twice. */
        if (movedX > DRAG_TOLERANCE_PX || movedY > DRAG_TOLERANCE_PX) return;
      }

      const selection = window.getSelection();
      if (selection && selection.toString().length > 0) return;

      this.step(this.isRightHalf(event) ? 1 : -1);
    }

    onKeyDown(event) {
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.target.closest('input, select, textarea, [contenteditable]')) return;

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.step(1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.step(-1);
      }
    }

    /* ---- autoplay ---- */

    setupAutoplay() {
      if (this.track.dataset.autoplay !== 'true') return;

      this.autoplaySpeed = (Number(this.track.dataset.speed) || 5) * 1000;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.playing = false;

      this.root.addEventListener('mouseenter', () => this.pause());
      this.root.addEventListener('mouseleave', () => this.play());
      this.root.addEventListener('focusin', () => this.pause());
      this.root.addEventListener('focusout', () => this.play());

      if (this.autoplayButton) {
        this.autoplayButton.addEventListener('click', () => {
          this.userPaused = !this.userPaused;
          this.autoplayButton.classList.toggle('slideshow__autoplay--paused', this.userPaused);
          this.userPaused ? this.pause() : this.play();
        });
      }

      this.reducedMotion.addEventListener('change', () => (this.reducedMotion.matches ? this.pause() : this.play()));
      this.play();
    }

    play() {
      if (!this.autoplaySpeed || this.userPaused || this.reducedMotion.matches) return;
      this.pause();
      this.timer = setInterval(() => this.step(1), this.autoplaySpeed);
      this.playing = true;
    }

    pause() {
      if (this.timer) clearInterval(this.timer);
      this.timer = null;
      this.playing = false;
    }

    /* Interacting with the carousel should reset the countdown, not land you
       mid-slide because autoplay fired a moment after your click. */
    restartAutoplay() {
      if (this.playing) this.play();
    }
  }

  function init(root) {
    (root || document).querySelectorAll('slideshow-component[data-tap-nav]').forEach((component) => {
      if (component.dataset.tapNavReady) return;
      new OvaranaSlideshow(component);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  /* The theme editor re-renders the section wholesale; rebind on new markup. */
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
